import { User } from '../types/user';
import { AppConfig } from '../config';
import { fetchWithAuth } from './fetchWithAuth';

export type LoginResponse =
  | { action: 'authenticated'; accessToken: string; deviceKey?: string }
  | { action: 'mfa-required'; session: string; email: string }
  | { action: 'setup'; setupToken: string };

const LEGACY_DEVICE_KEY_STORAGE = 'mfaDeviceKey';
const DEVICE_KEY_PREFIX = 'mfaDeviceKey_';

function getDeviceKey(email?: string): string | undefined {
  if (email) {
    const emailKey = localStorage.getItem(`${DEVICE_KEY_PREFIX}${email.toLowerCase()}`);
    if (emailKey) return emailKey;
  }
  return localStorage.getItem(LEGACY_DEVICE_KEY_STORAGE) || undefined;
}

function storeDeviceKey(email: string, deviceKey: string): void {
  localStorage.setItem(`${DEVICE_KEY_PREFIX}${email.toLowerCase()}`, deviceKey);
}

class AuthApi {
  async login(email: string, password: string): Promise<LoginResponse> {
    const deviceKey = getDeviceKey(email);
    const response = await fetch(`${AppConfig.apiRoot}auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, deviceKey }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Invalid credentials');
    }
    const result = await response.json();
    if (result.action === 'authenticated' && result.deviceKey) {
      storeDeviceKey(email, result.deviceKey);
    }
    return result;
  }

  async verifyMfa(email: string, session: string, totpCode: string): Promise<{ accessToken: string; deviceKey?: string }> {
    const response = await fetch(`${AppConfig.apiRoot}auth/verify-mfa`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, session, totpCode }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'MFA verification failed');
    }
    const result = await response.json();
    if (result.deviceKey) {
      storeDeviceKey(email, result.deviceKey);
    }
    return result;
  }

  me(): Promise<User> {
    return fetchWithAuth(`${AppConfig.apiRoot}me/info`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }).then((resp) => {
      if (resp.status !== 200) {
        throw new Error('');
      }
      return resp.json();
    });
  }

  async getSettings(): Promise<{ tgBotName: string; cognitoPoolId?: string; cognitoClientId?: string }> {
    return fetch(`${AppConfig.apiRoot}auth/settings`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    }).then((resp) => {
      if (resp.status !== 200) {
        throw new Error('');
      }
      return resp.json();
    });
  }

  async getSetupStatus(token: string): Promise<{
    step: 'password' | 'mfa' | 'completed';
    email?: string;
  }> {
    const response = await fetch(`${AppConfig.apiRoot}auth/setup-status?token=${encodeURIComponent(token)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Invalid or expired token');
    }
    return response.json();
  }

  async completeAccountSetup(
    token: string,
    password: string
  ): Promise<{
    userId: string;
    email: string;
    requiresMfaSetup: boolean;
    passwordAlreadySet?: boolean;
    alreadyCompleted?: boolean;
  }> {
    const response = await fetch(`${AppConfig.apiRoot}auth/complete-setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to complete setup');
    }
    return response.json();
  }

  async initiateMfaSetup(
    setupToken: string,
    password: string
  ): Promise<{
    secretCode: string;
    totpUri: string;
    session: string;
    accessToken?: string;
  }> {
    const response = await fetch(`${AppConfig.apiRoot}auth/mfa/setup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ setupToken, password }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to initiate MFA setup');
    }
    return response.json();
  }

  async verifyMfaSetup(
    setupToken: string,
    session: string,
    totpCode: string,
    accessToken?: string
  ): Promise<{
    success: boolean;
    userId: string;
  }> {
    const response = await fetch(`${AppConfig.apiRoot}auth/mfa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ setupToken, session: session || undefined, accessToken, totpCode }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Invalid verification code');
    }
    return response.json();
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await fetch(`${AppConfig.apiRoot}auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to process request');
    }
    return response.json();
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    const response = await fetch(`${AppConfig.apiRoot}auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, newPassword }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to reset password');
    }
  }

  async impersonate(targetUserId: string): Promise<{ accessToken: string }> {
    const response = await fetchWithAuth(`${AppConfig.apiRoot}auth/impersonate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ targetUserId }),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Impersonation failed');
    }
    return response.json();
  }
}

export const authApi = new AuthApi();
